<template>
  <div class="w-full flex items-center">
    <div class="h-[40px]">
      <slot name="prefix" v-if="$slots.prefix" />
    </div>

    <div class="w-full">
      <label
        v-if="label"
        :for="inputId"
        class="block mb-1.5 text-sm font-medium text-heading dark:text-heading-dark"
      >
        {{ label }}
      </label>
      <div class="relative">
        <div
          v-if="$slots.leftComponent"
          class="absolute left-[15px] top-1/2 -translate-y-1/2 flex items-center justify-center"
        >
          <slot name="leftComponent" />
        </div>

        <input
          :id="inputId"
          :type="inputType"
          :value="modelValue"
          :placeholder="placeholder"
          :name="name"
          :required="required"
          :disabled="disabled"
          :aria-invalid="error ? true : undefined"
          :aria-describedby="error ? errorId : undefined"
          @input="onInput"
          ref="inputRef"
          v-bind="$attrs"
          class="w-full h-[40px] py-[10px] bg-input-bg dark:bg-input-bg-dark border border-container-border dark:border-container-border-dark text-heading dark:text-heading-dark placeholder:text-muted-text dark:placeholder:text-muted-text-dark transition duration-150 ease-in-out focus:outline-none disabled:cursor-not-allowed disabled:border-container-border dark:disabled:border-container-border-dark disabled:text-muted-text dark:disabled:text-muted-text-dark"
          :class="{
            'rounded-r-md': $slots.prefix,
            'rounded-md': !$slots.prefix,
            'pl-[50px]': $slots.leftComponent,
            'pr-[40px]': $slots.rightComponent || type === 'password',
            'border-danger-border dark:border-danger-border-dark focus:border-danger-border dark:focus:border-danger-border-dark focus:ring-2 focus:ring-danger-border/30 dark:focus:ring-danger-border-dark/30':
              error,
            'focus:border-accent focus:ring-2 focus:ring-accent/25':
              !error,
          }"
        />

        <button
          v-if="type === 'password'"
          type="button"
          class="absolute right-[15px] top-1/2 -translate-y-1/2 flex items-center justify-center rounded text-muted-text dark:text-muted-text-dark hover:text-content dark:hover:text-content-dark focus:outline-none focus-visible:ring-2 focus-visible:ring-main-button-bg/40"
          :aria-label="
            $t(
              showPassword
                ? 'components.input.hidePassword'
                : 'components.input.showPassword',
            )
          "
          :aria-pressed="showPassword"
          @click="togglePasswordVisibility"
        >
          <MdiEye v-if="!showPassword" class="w-5 h-5" aria-hidden="true" />
          <MdiEyeOff v-else class="w-5 h-5" aria-hidden="true" />
        </button>

        <div
          v-else-if="$slots.rightComponent"
          class="absolute right-[15px] top-1/2 -translate-y-1/2 flex items-center justify-center"
        >
          <slot name="rightComponent" />
        </div>
      </div>
      <div
        v-if="error"
        :id="errorId"
        class="text-xs text-danger-text dark:text-danger-text-dark mt-1"
      >
        {{ error }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import MdiEye from "~icons/mdi/eye";
import MdiEyeOff from "~icons/mdi/eye-off";

defineOptions({
  inheritAttrs: false,
});

interface Props {
  modelValue?: string | number;
  label?: string;
  placeholder?: string;
  type?: string;
  id?: string;
  name?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string | null;
}

const props = withDefaults(defineProps<Props>(), {
  type: "text",
  label: "",
  placeholder: "",
  id: undefined,
  name: undefined,
  required: false,
  disabled: false,
  error: null,
});

const emit = defineEmits(["update:modelValue", "change"]);

const inputRef = ref<HTMLInputElement | null>(null);
const showPassword = ref(false);

const inputId = computed(
  () => props.id || `input-${Math.random().toString(36).substring(7)}`,
);

const errorId = computed(() => `${inputId.value}-error`);

const inputType = computed(() => {
  if (props.type === "password" && showPassword.value) {
    return "text";
  }
  return props.type;
});

const togglePasswordVisibility = () => {
  showPassword.value = !showPassword.value;
};

const onInput = (event: Event) => {
  const target = event.target as HTMLInputElement;
  emit("update:modelValue", target.value);
  emit("change", target.value);
};

defineExpose({
  focus: () => inputRef.value?.focus(),
  blur: () => inputRef.value?.blur(),
});
</script>
